import { AmpSettingsMap, UserCoverageReport, UserCoverageSummary } from '@ampsec/platform-client';
import type { ColDef } from 'ag-grid-enterprise';
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  TooltipModule,
} from 'ag-grid-community';
import { getConnectorSettings, getUserCoverageReport } from 'Apis/library';
import EngageIcon from 'Assets/icons/engagement.svg';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useI18n } from 'Hooks/useI18n';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AgGridBase } from '../AgGridBase';
import { DEFAULT_BASE_COL_DEF } from '../AgGridBase/AgGridBase.config';
import { DEFAULT_COL_DEF, GRID_CONFIG, GridContext } from '../AgToolingComplianceGrid/AgGridCompliance.config';
import { buildUserAssetCoverageRows } from './buildUserAssetCoverageRows';
import userAssetCoverageColumns from './getUserAssetCoverageColumns';
import '../AgToolingComplianceGrid/styles.scss';

ModuleRegistry.registerModules([ClientSideRowModelModule, TooltipModule, RenderApiModule, RowApiModule]);

interface UserAssetCoverageGridProps {
  userId: string;
}

const UserAssetCoverageGrid: React.FC<UserAssetCoverageGridProps> = ({ userId }) => {
  const { contentfulData } = useContext(ContentfulContext);
  const providerNameIntegrationMap = contentfulData?.providerNameIntegrationMap;
  const installedConnectorMap = useSelector((state: { installedConnectorMap: GridContext['allConnectors'] }) => state.installedConnectorMap);
  const { t } = useI18n();
  const connectorDataHook = useConnectorData();

  const { data: tenantSettings, isLoading: isSettingsLoading } = useApiObjectData<AmpSettingsMap, AmpSettingsMap>(
    'CONNECTOR_SETTINGS',
    getConnectorSettings,
    (settings) => settings,
  );

  const { data: rows, isLoading: isCoverageLoading } = useApiObjectData<
    UserCoverageReport,
    UserCoverageSummary[]
  >(
    `user-coverage-${userId}`,
    () => getUserCoverageReport(userId),
    buildUserAssetCoverageRows,
    undefined,
    !!userId,
  );

  const gridContext = useMemo<GridContext>(
    () => ({
      tenantSettings,
      providerNameIntegrationMap,
      isLoading: isSettingsLoading || connectorDataHook.isFetchingConnectors || isCoverageLoading,
      allConnectors: installedConnectorMap,
      connectorDataHook,
      t,
    }),
    [
      tenantSettings,
      providerNameIntegrationMap,
      isSettingsLoading,
      connectorDataHook,
      isCoverageLoading,
      installedConnectorMap,
      t,
    ],
  );

  const coverageRows = rows ?? [];
  const manyRows = coverageRows.length > 3;

  const stableDefaultColDef = useMemo(
    () =>
      ({
        ...DEFAULT_BASE_COL_DEF,
        ...DEFAULT_COL_DEF,
        resizable: false,
        sortable: true,
      }) as ColDef<UserCoverageSummary>,
    [],
  );

  return (
    <div className="user-asset-coverage-grid">
      <AgGridBase.Root>
        <AgGridBase.Table<UserCoverageSummary>
          columnDefs={userAssetCoverageColumns as ColDef<UserCoverageSummary>[]}
          dataMode="client"
          rowData={coverageRows}
          showSidebar={false}
          bodyHeight={manyRows ? '12rem' : 'auto'}
          defaultColDef={stableDefaultColDef}
          agGridProps={{
            context: gridContext,
            suppressCellFocus: true,
            domLayout: manyRows ? 'normal' : 'autoHeight',
            rowHeight: GRID_CONFIG.rowHeight,
            headerHeight: GRID_CONFIG.headerHeight,
            getRowId: (params) => String(params.data?.id ?? params.data?.assets?.[0]?.id ?? ''),
            loading: isCoverageLoading || isSettingsLoading,
            noRowsOverlayComponentParams: {
              ImageSrc: EngageIcon,
              noDescription: false,
              description: 'No assets found',
            },
          }}
        />
      </AgGridBase.Root>
    </div>
  );
};

export default UserAssetCoverageGrid;
